import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef, OnDestroy, EventEmitter } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { Form } from "src/modules/common/interfaces/form.interface";
import { Warehouse } from "src/modules/common/interfaces/warehouse.interface";
import { Api } from "src/modules/common/services/api.service";
import { Api3Service } from "src/modules/common/services/api3.service";
import { PartnerService } from "src/modules/common/services/partner.service";
import { SpinnerService } from "src/modules/common/services/spinner.service";
import { Modal } from "src/modules/section/services/modal.service";

@Component({
    selector: "partner-packages-boxes-add-item-form",
    templateUrl: "add-item-form.component.html",
    styleUrls: ["add-item-form.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PackageBoxesAddItemFormComponent implements OnInit, OnDestroy {
    public modal: Modal.IModal;

    public inventories: any[] = [];
    public inventoriesFiltered: any[] = [];
    public inventoriesSearch: FormControl = new FormControl(null);
    public itemSearch: FormControl = new FormControl(null);
    public items: any[] = [];
    public filteredItems: any[] = [];

    public formGroup: FormGroup = new FormGroup({
        item: new FormControl(null, [Validators.required]),
        inventories_ids: new FormControl([], [Validators.required]),
    });

    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    public constructor(
        private apiV3Service: Api3Service,
        private changeDetectorRef: ChangeDetectorRef,
        private spinnerService: SpinnerService) {
    }

    private async getItems(locationId: number): Promise<any> {
        this.spinnerService.show();
        const { data }: Api.IResponse = await this.apiV3Service.request(Api.EMethod.Get,
            `partner/${PartnerService.partner.slug}/inventories`, {}, {
            no_box: true,
            location_id: locationId,
            available_only: true,
            relations: ["WarehouseLocation:id,location"]
        });
        if (data && data.length) {
            this.inventories = Object.values(data);
            this.inventoriesFiltered = [];
            this.items = data.map(inventory => {
                return inventory.item;
            }).filter((value, index, array) => array.indexOf(value) === index);
            this.filteredItems = this.items;
        }
        this.changeDetectorRef.markForCheck();
        this.spinnerService.hide();
    }

    public async submit(): Promise<void> {
        await this.apiV3Service.request(Api.EMethod.Post,
            `partner/${PartnerService.partner.slug}/boxes/${this.modal.params.boxId}/attach_item`,
            this.formGroup.value
        );
        this.modal.response.next({ name: "list" });
    }

    public toggleSelection(inventoryId: number, event: any): void {
        const inventories = this.formGroup.get("inventories_ids")?.value || [];
        if (event.checked) {
          inventories.push(inventoryId);
        } else {
          const index = inventories.indexOf(inventoryId);
          if (index >= 0) {
            inventories.splice(index, 1);
          }
        }
        this.formGroup.get("inventories_ids")?.setValue(inventories);
    }
    
    public get inventories_ids(): any[]|null {
        return this.formGroup.get("inventories_ids")?.value;
    }

    public close(): void {
        this.modal.response.next(null);
    }

    public async ngOnInit(): Promise<void> {
        if (this.modal.params.locationId) {
            this.getItems(this.modal.params.locationId);
        };

        this.formGroup.controls["item"].valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
            this.inventoriesFiltered = this.inventories.filter(inventory => inventory.item === value);
            this.changeDetectorRef.markForCheck();
        });

        this.itemSearch.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: string): void => {
                if (value) {
                    this.filteredItems = this.items.filter(
                        item => item.toLowerCase().indexOf(value.toLowerCase()) > -1
                    );
                } else {
                    this.filteredItems = this.items;
                }
                this.changeDetectorRef.markForCheck();
            });

        this.inventoriesSearch.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe((value: string): void => {
                if (value) {
                    this.inventoriesFiltered = this.inventoriesFiltered.filter(
                        inventory => inventory.batch?.toLowerCase().indexOf(value.toLowerCase()) > -1
                            || inventory.serial?.toLowerCase().indexOf(value.toLowerCase()) > -1
                            || inventory.lot?.toLowerCase().indexOf(value.toLowerCase()) > -1
                            || inventory.rev?.toLowerCase().indexOf(value.toLowerCase()) > -1
                    );
                } else {
                    const selectedItem = this.formGroup.controls["item"].value;
                    this.inventoriesFiltered = this.inventories.filter(
                        inventory => inventory.item === selectedItem
                    );
                }
                this.changeDetectorRef.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}